<template>
    <Popover
        v-slot="{}"
        class="relative"
    >
        <PopoverButton
            ref="popoverButtonRef"
            class="focus:outline-hidden"
            data-spec-class="support-button__trigger"
        >
            <div
                class="-mb-1.5 rounded-t-md bg-secondary--inverse px-4 py-2 whitespace-nowrap text-secondary transition duration-300 ease-out hover:-translate-y-1.5 ui-open:transform ui-open:opacity-0"
            >
                {{ t('actions.support') }}
            </div>
        </PopoverButton>

        <PopoverPanel
            v-slot="{ close }"
            class="absolute bottom-6 z-20 w-80 -translate-x-1/2 rotate-90"
            data-spec-class="support-button__panel"
        >
            <div class="rounded-xl border border-divider bg-surface--elevated p-6 shadow-lg">
                <div class="absolute top-0 right-0 mr-3 hidden pt-3 sm:block">
                    <BaseButtonClose
                        data-spec-class="support-button__close"
                        @click="onClose(close)"
                    />
                </div>
                <div class="mb-6">
                    <h2 class="text-lg text-brand">
                        {{ t('pages.DashboardPage.contactTeaser.title') }}
                    </h2>
                    <div class="space-y-1">
                        <div class="flex justify-between">
                            <span>
                                {{ t('pages.DashboardPage.contactTeaser.phone') }}
                            </span>
                            <BaseLink :href="`tel:${contact.phone.general.densed}`">
                                {{ contact.phone.general.pretty }}
                            </BaseLink>
                        </div>

                        <div class="flex justify-between">
                            <span>
                                {{ t('pages.DashboardPage.contactTeaser.mail') }}
                            </span>
                            <div class="flex flex-col items-end">
                                <BaseLink :href="hrefSupport">
                                    {{ contact.mail.support }}
                                </BaseLink>
                                <BaseLink :href="hrefFinance">
                                    {{ contact.mail.finance }}
                                </BaseLink>
                            </div>
                        </div>

                        <div class="flex justify-between">
                            <span>
                                {{ t('pages.DashboardPage.contactTeaser.emergency') }}
                            </span>
                            <BaseLink :href="hrefNoc">
                                {{ contact.mail.noc }}
                            </BaseLink>
                        </div>
                    </div>
                </div>

                <div class="mb-6">
                    <div class="flex items-center">
                        <h2 class="text-lg text-brand">
                            {{ t('pages.DashboardPage.serviceTeaser.title') }}
                        </h2>
                        <BusinessHoursTooltip />
                    </div>
                    <div class="text-base">
                        <div class="flex justify-between">
                            <span class="mb-1">
                                {{ t('pages.DashboardPage.serviceTeaser.mondayToThursday') }}
                            </span>
                            <span>
                                {{
                                    t(
                                        'pages.DashboardPage.serviceTeaser.mondayToThursdayOpeningHours'
                                    )
                                }}
                            </span>
                        </div>
                        <div class="flex justify-between">
                            <span>
                                {{ t('pages.DashboardPage.serviceTeaser.friday') }}
                            </span>
                            <span>
                                {{ t('pages.DashboardPage.serviceTeaser.fridayOpeningHours') }}
                            </span>
                        </div>
                    </div>
                </div>

                <div>
                    <div class="flex items-center">
                        <h2 class="text-lg text-brand">
                            {{ t('pages.TicketIndexPage.title') }}
                        </h2>
                    </div>
                    <BaseButton
                        data-spec-class="ticket-new"
                        :size="SizeAll.Tiny"
                        @click="openTicketDrawer"
                    >
                        {{ t('resources.ticket.actions.create') }}</BaseButton
                    >
                </div>
            </div>
        </PopoverPanel>
    </Popover>
</template>

<script setup lang="ts">
import { ref, watch, defineExpose } from 'vue';

import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue';
import { useI18n } from 'vue-i18n';
import BaseButtonClose from './BaseButtonClose.vue';
import { contact } from '@/config/data';
import BusinessHoursTooltip from '@/modules/your/components/BusinessHoursTooltip.vue';
import { SizeAll } from '@/types';
import { useSupport } from '@/modules/your/composables/support';
import { useTicket } from '@/modules/your/composables/ticket';

const { t } = useI18n();
const { isSupportOverlayOpen, closeSupportOverlay } = useSupport();
const { openTicketDrawer } = useTicket();

const popoverButtonRef = ref();
const isOpen = ref(false);

watch(isSupportOverlayOpen, (newValue) => {
    if (newValue && popoverButtonRef.value) {
        popoverButtonRef.value.$el.click();
        closeSupportOverlay();
    }
});

const onClose = (close) => {
    isOpen.value = false;
    close();
};

const openSupportButton = () => {
    if (popoverButtonRef.value) {
        popoverButtonRef.value.$el.click();
    }
};

defineExpose({
    openSupportButton,
});

const hrefSupport = ref(`mailto:${contact.mail.support}`);
const hrefFinance = ref(`mailto:${contact.mail.finance}`);
const hrefNoc = ref(`mailto:${contact.mail.noc}`);
</script>
